import { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { DividendFrequency, DividendPayoutStatus } from '../../../hooks/dividend/dividend.types';
import { StakeholderDividendPayout } from '../../../hooks/dividend/use-dividend-stakeholder-payouts.hook';
import { getTranslation } from '../../../utils/get-translation.util';
import {
  formatDividendAmount,
  formatDividendPerShare,
} from '../../dividends/utils/format-dividend-amount.util';
import {
  BreakdownColumn,
  breakdownColumnOrderMap,
  breakdownColumnTranslationKey,
  Column,
  columnOrderMap,
} from './dividends-ledger.types';

const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const formatPeriod = (
  frequency: DividendFrequency,
  year: number,
  periodIndex: null | number,
): string => {
  if (frequency === DividendFrequency.ANNUALLY) return `Annual ${year}`;
  if (frequency === DividendFrequency.QUARTERLY) return `Q${periodIndex ?? ''} ${year}`;
  if (frequency === DividendFrequency.SEMI_ANNUALLY) return `H${periodIndex ?? ''} ${year}`;
  if (frequency === DividendFrequency.MONTHLY && periodIndex) {
    return `${MONTH_ABBR[periodIndex - 1]} ${year}`;
  }
  return String(year);
};

export const formatDate = (iso: null | string): string => {
  if (!iso) return '—';
  // Day-first dd/MM/yyyy (en-GB), matching the rest of the app instead of US month-first.
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const statusStyleMap: Record<
  DividendPayoutStatus,
  { bg: string; label: string; text: string }
> = {
  [DividendPayoutStatus.APPROVED]: {
    bg: 'bg-brand-50',
    label: getTranslation('DIVIDEND_STATUS_APPROVED'),
    text: 'text-brand-700',
  },
  [DividendPayoutStatus.PAID_OUT]: {
    bg: 'bg-forest-50',
    label: getTranslation('DIVIDEND_STATUS_PAID_OUT'),
    text: 'text-forest-600',
  },
  [DividendPayoutStatus.PENDING]: {
    bg: 'bg-sunbeam-50',
    label: getTranslation('DIVIDEND_STATUS_PENDING'),
    text: 'text-sunbeam-700',
  },
};

export type PayoutGroup = {
  frequency: DividendFrequency;
  id: string;
  items: StakeholderDividendPayout[];
  name: string;
  payoutDate: null | string;
  periodIndex: null | number;
  status: DividendPayoutStatus;
  totalAmount: number;
  year: number;
};

const cellClass = 'relative min-w-max px-2';
// The header cell is a plain `px-2` th, so padding the inner wrapper too pushed every value 8px
// right of its column title.
const cellInnerClass = 'flex h-14 items-center';
// Figma sets this table at 14/20 (Desktop/text-sm), not the 12px the rest of the row used.
const labelClass = 'text-sm font-[450] text-gray-700';

type Props = {
  currencySymbol: string;
  group: PayoutGroup;
  isExpanded: boolean;
  onToggle: () => void;
};

export const DividendsLedgerItem: FC<Props> = ({ group, isExpanded, onToggle, currencySymbol }) => {
  const hasBreakdown = group.items.some((i) => i.shareClassName !== null) || group.items.length > 1;
  const style = statusStyleMap[group.status];

  const cellContent: Record<Column, React.ReactNode> = {
    [Column.CHEVRON]: hasBreakdown ? (
      <ChevronDownIcon
        className={twMerge(
          'h-4 w-4 transition-transform',
          isExpanded ? 'rotate-180 text-brand-700' : 'text-gray-400',
        )}
      />
    ) : null,
    [Column.PAYOUT_NAME]: group.name,
    [Column.PERIOD]: formatPeriod(group.frequency, group.year, group.periodIndex),
    [Column.PAYOUT_DATE]: formatDate(group.payoutDate),
    [Column.TOTAL_DIVIDEND_PAYOUT]: (
      // The row's headline figure, weighted like the per-class total it sums.
      <span className="font-[550] text-gray-800">
        {currencySymbol} {formatDividendAmount(group.totalAmount)}
      </span>
    ),
    [Column.STATUS]: (
      <span
        className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-[550] ${style.bg} ${style.text}`}
      >
        {style.label}
      </span>
    ),
  };

  const breakdownContent: Record<
    BreakdownColumn,
    (item: StakeholderDividendPayout) => React.ReactNode
  > = {
    [BreakdownColumn.SHARE_CLASS]: (item) => (
      <span className="block max-w-[200px] truncate" title={item.shareClassName ?? undefined}>
        {item.shareClassName ?? '—'}
      </span>
    ),
    [BreakdownColumn.DIVIDEND_PER_SHARE]: (item) =>
      item.amountPerShare !== null ? formatDividendPerShare(item.amountPerShare) : '—',
    [BreakdownColumn.SHARE_AMOUNT]: (item) =>
      item.stakeholderShares !== null ? item.stakeholderShares.toLocaleString('en-US') : '—',
    [BreakdownColumn.PAYOUT_FOR_SHARE_CLASS]: (item) => (
      <span className="font-[550] text-forest-600">
        {currencySymbol} {formatDividendAmount(item.itemAmount)}
      </span>
    ),
  };

  return (
    <>
      <tr
        className={twMerge(
          'cursor-pointer border-b border-[#F2F2F2] transition-all hover:bg-gray-50',
          isExpanded && 'bg-brand-25 hover:bg-brand-25',
        )}
        onClick={onToggle}
      >
        {columnOrderMap.map((col, index) => {
          const isName = col === Column.PAYOUT_NAME;

          return (
            <td
              className={twMerge(
                cellClass,
                isExpanded && index === 0 && 'border-l-2 border-brand-700',
              )}
              key={col}
            >
              <div className={cellInnerClass}>
                <span
                  className={twMerge(labelClass, isName && 'block max-w-[220px] truncate')}
                  title={isName ? group.name : undefined}
                >
                  {cellContent[col]}
                </span>
              </div>
            </td>
          );
        })}
      </tr>

      {isExpanded && hasBreakdown && (
        <tr className="border-b border-[#F2F2F2] bg-white">
          <td className="px-8 py-3" colSpan={columnOrderMap.length}>
            <div className="overflow-hidden rounded-md border border-gray-100 bg-white">
              <table className="w-full">
                <thead className="border-b border-[#F2F2F2] bg-[#F9FAFB]">
                  <tr>
                    {breakdownColumnOrderMap.map((col) => (
                      <th
                        className="px-3 py-2 text-left text-sm font-medium text-[#172335]"
                        key={col}
                      >
                        <AppFormattedMessage id={breakdownColumnTranslationKey[col]} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F2F2F2]">
                  {group.items.map((item, idx) => (
                    <tr key={idx}>
                      {breakdownColumnOrderMap.map((col) => (
                        <td className="px-3 py-2 text-sm text-gray-700" key={col}>
                          {breakdownContent[col](item)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
